import React, { useState, useEffect } from 'react';
import { LogOut, Settings, MessageCircle, Share2, TrendingUp, Award, Package } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getArtworksByArtist } from '../services/artworkService';
import { getWishlistArtworks } from '../services/wishlistService';
import { getFollowersCount, getFollowingCount } from '../services/followService';
import { getOrdersByBuyer } from '../services/orderService'; // Using your existing function

import { ArtworkGrid } from '../components/artwork/ArtworkGrid';
import { PurchaseCard } from '../components/order/PurchaseCard';
import { Button } from '../components/common/Button';
import { useFollow } from '../hooks/useFollow';
import { Artwork, User, Order } from '../types';
import { motion } from 'framer-motion';

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  const profileUserId = userId || user?.id || '';
  const isOwnProfile = !userId || userId === user?.id;
  
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [wishlistArtworks, setWishlistArtworks] = useState<Artwork[]>([]);
  const [purchases, setPurchases] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'posts' | 'wishlist' | 'purchases'>('posts');
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  const { isFollowingUser, toggleFollow, loading: followLoading } = useFollow(profileUserId);

  useEffect(() => {
    if (profileUserId) {
      loadProfileData();
      loadStats();
    }
  }, [profileUserId]);

  useEffect(() => {
    if (activeTab === 'purchases' && isOwnProfile && user && purchases.length === 0) {
      loadPurchases();
    }
  }, [activeTab, isOwnProfile, user]);

  const loadProfileData = async () => {
    setLoading(true);
    try {
      // Load user data if viewing another profile
      if (!isOwnProfile) {
        const userDoc = await getDoc(doc(db, 'users', profileUserId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfileUser({ 
            id: userDoc.id, 
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
          } as User);
        }
      } else {
        setProfileUser(user);
      }

      const artworksData = await getArtworksByArtist(profileUserId);
      setArtworks(artworksData);
      
      if (isOwnProfile && user) {
        const wishlistData = await getWishlistArtworks(user.id);
        setWishlistArtworks(wishlistData);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPurchases = async () => {
    if (!user) return;
    
    setPurchasesLoading(true);
    try {
      // Using your existing getOrdersByBuyer function
      const purchasesData = await getOrdersByBuyer(user.id);
      setPurchases(purchasesData);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [followers, following] = await Promise.all([
        getFollowersCount(profileUserId),
        getFollowingCount(profileUserId),
      ]);
      setFollowersCount(followers);
      setFollowingCount(following);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profileUser?.name}'s Profile`,
          text: `Check out ${profileUser?.name}'s amazing artworks on ANI!`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard!');
    }
  };

  const handleWhatsAppContact = (phoneNumber?: string) => {
    const phone = phoneNumber || '919876543210';
    const message = encodeURIComponent('Hi! I want to connect regarding your artwork on ANI.');
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const displayUser = profileUser || user;

  if (!displayUser) return null;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowLogoutModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="text-red-500" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Sign Out</h3>
              <p className="text-gray-500 mb-6">Are you sure you want to sign out of your account?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 py-3 px-4 border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex-1 py-3 px-4 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-r from-primary via-purple-600 to-pink-500 p-6 text-white relative overflow-hidden"
      >
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="w-24 h-24 bg-white rounded-full overflow-hidden shadow-2xl ring-4 ring-white ring-opacity-50"
              >
                {displayUser.avatar ? (
                  <img src={displayUser.avatar} alt={displayUser.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-primary text-3xl font-bold">
                    {displayUser.name.charAt(0)}
                  </div>
                )}
              </motion.div>
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  {displayUser.name}
                  {displayUser.isPromoted && (
                    <Award className="text-yellow-400" size={24} />
                  )}
                </h2>
                <p className="text-sm opacity-90">{displayUser.email}</p>
                {displayUser.isPromoted && (
                  <span className="inline-block mt-1 px-3 py-1 bg-yellow-400 text-primary text-xs font-bold rounded-full">
                    PREMIUM
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {isOwnProfile ? (
                <>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/analytics')}
                    className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors backdrop-blur-sm"
                    title="Analytics"
                  >
                    <TrendingUp size={20} />
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowLogoutModal(true)}
                    className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors backdrop-blur-sm"
                    title="Sign Out"
                  >
                    <LogOut size={20} />
                  </motion.button>
                </>
              ) : (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleShare}
                  className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors backdrop-blur-sm"
                  title="Share Profile"
                >
                  <Share2 size={20} />
                </motion.button>
              )}
            </div>
          </div>
          
          {displayUser.bio && (
            <p className="text-sm opacity-90 mb-4">{displayUser.bio}</p>
          )}

          <div className="flex gap-8 text-sm">
            <div>
              <span className="font-bold text-lg">{artworks.length}</span>
              <span className="opacity-90 ml-1">Posts</span>
            </div>
            <div>
              <span className="font-bold text-lg">{followersCount}</span>
              <span className="opacity-90 ml-1">Followers</span>
            </div>
            <div>
              <span className="font-bold text-lg">{followingCount}</span>
              <span className="opacity-90 ml-1">Following</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Actions */}
      <div className="p-4 bg-white">
        {isOwnProfile ? (
          <div className="space-y-3">
            <div className="flex gap-3">
              <Button onClick={() => navigate('/post')} fullWidth className="shadow-lg">
                Post Artwork
              </Button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/edit-profile')}
                className="px-6 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors shadow-md"
              >
                <Settings size={20} />
              </motion.button>
            </div>
            
            {/* Seller Dashboard Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/seller/dashboard')}
              className="w-full px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Package size={20} />
              Seller Dashboard
            </motion.button>

            {/* Logout Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowLogoutModal(true)}
              className="w-full px-6 py-4 bg-gray-100 text-red-500 rounded-xl font-medium hover:bg-red-50 transition-all flex items-center justify-center gap-2 border border-gray-200"
            >
              <LogOut size={20} />
              Sign Out
            </motion.button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button 
              onClick={toggleFollow} 
              disabled={followLoading}
              fullWidth
              variant={isFollowingUser ? 'outline' : 'primary'}
              className="shadow-lg"
            >
              {isFollowingUser ? '✓ Following' : '+ Follow'}
            </Button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => handleWhatsAppContact(displayUser.phone)}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md flex items-center gap-2"
            >
              <MessageCircle size={20} />
              WhatsApp
            </motion.button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10">
        {['posts', 'wishlist', 'purchases'].map((tab) => (
          (isOwnProfile || tab === 'posts') ? (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as 'posts' | 'wishlist' | 'purchases')}
              className={`flex-1 py-4 font-medium transition-all duration-300 ${
                activeTab === tab
                  ? 'text-primary border-b-2 border-primary bg-gradient-to-t from-purple-50'
                  : 'text-gray-500'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ) : null
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'posts' && (
          <ArtworkGrid artworks={artworks} loading={loading} />
        )}
        
        {activeTab === 'wishlist' && (
          <ArtworkGrid artworks={wishlistArtworks} loading={loading} />
        )}
        
        {activeTab === 'purchases' && (
          <div>
            {purchasesLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="text-gray-500 mt-4">Loading purchases...</p>
              </div>
            ) : purchases.length > 0 ? (
              <div className="space-y-4">
                {purchases.map((order) => (
                  <PurchaseCard key={order.id} order={order} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
                <div className="text-8xl mb-4">🛍️</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">No purchases yet</h3>
                <p className="text-gray-500 mb-6">Start exploring artworks to make your first purchase!</p>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors shadow-lg"
                >
                  Browse Artworks
                </motion.button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;